/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useCallback, useRef, useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import "./BoostOnboardingStyle.css";
import { ToggleVisibility, Checkbox } from "components/common";
import { useApolloClient } from "@apollo/react-hooks";
import moment from 'moment';
import { runPasswordValidator } from 'utils/runPasswordValidator';
import md5 from "md5";
import { parsePhoneNumberFromString, isPossiblePhoneNumber } from "libphonenumber-js";
import { GOOGLE_ADDRESS_TYPES, STATES } from "./consts";
import { VALIDATE_ADDRESS, SUBMIT_BASIC_USER_INFO } from "./mutations";
import CustomPhoneNumberInput from "./components/CustomPhoneNumberInput";
import CustomDatePicker from "./components/CustomDatePicker";
import { LegalAgreement } from "../../common/LegalAgreement";
import { InviteFriends } from "./components";

// ERROR MESSAGES
const NO_VALUE_ERROR_MESSAGE = "This field is required";
const DOB_MIN_AGE_ERROR_MESSAGE = "Must be over 18 years old";
const INVALID_PHONE_NUMBER_ERROR_MESSAGE = "Please enter valid phone number";
const INVALID_SSN_ERROR_MESSAGE = "Invalid SSN";

// VALIDATION REGEX
const nameRegex = /^[A-Za-z ,.'-]+$/i;
const numericalRegex = /\D/g;

const formatSSN = (ssn = "") => {
  const cleaned = (ssn).replace(numericalRegex, "");
  let formattedSSN = "";

  // allow user to enter "-" manually.
  if (
    (ssn.length === 4 && ssn[3] === "-") ||
    (ssn.length === 7 && ssn[6] === "-")
  ) {
    return ssn;
  }

  // Check if the ssn contains only digits
  if (/^\d+$/.test(cleaned)) {
    formattedSSN = cleaned;

    // Add hyphen for the ssn format XXX-XX-XXXX
    if (cleaned.length > 3) {
      formattedSSN = `${cleaned.slice(0, 3)}-`;

      if (cleaned.length > 5) {
        formattedSSN += `${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
      } else {
        formattedSSN += `${cleaned.slice(3)}`;
      }
    }

    return formattedSSN;
  }

  return formattedSSN;
};

const buildInputWithLabel = ({ component, labelName, error, className = "" }) => {
  return (
    <div className={`${className}`}>
      <div className="mb-1 text-md font-semibold leading-normal">{labelName}</div>
      {component}
      <p className="text-sm text-red-600">{error}</p>
    </div>
  );
};

const BoostOnboarding = ({ stripeUser, user, setCompletedOnboarding, currentProductTerm, currentProductTier }) => {
  const client = useApolloClient();
  const defaultNameValues = stripeUser?.name?.split(" ") || [];
  const { referralCode = "" } = user;
  const defaultFirst = defaultNameValues.length ? defaultNameValues[0] : "" ;
  const defaultLast = defaultNameValues.length ? defaultNameValues.slice(1).join(" ") : "";
  const [firstName, setFirstName] = useState(user.firstName || defaultFirst);
  const [lastName, setLastName] = useState(user.lastName  || defaultLast);
  const [ssn, setSSN] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || stripeUser?.phone || "");
  const [dob, setDob] = useState(user.dob || "");
  const [errorMessage, setErrorMessage] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [dateOfBirthError, setDateOfBirthError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [ssnError, setSsnError] = useState("");
  const [address1Error, setAddress1Error] = useState("");
  const [localityError, setLocalityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [postcodeError, setPostcodeError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ssnInputType, setSsnInputType] = useState("password");
  const [createPasswordType, setCreatePasswordType] = useState("password");
  // const [confirmPasswordType, setConfirmPasswordType] = useState("password");
  const [addressInvalid, setAddressInvalid] = useState(false);
  const [bypassAddress, setBypassAddress] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [address1, setAddress1] = useState(user.addresses?.[0]?.street || "");
  const [address2, setAddress2] = useState(user.addresses?.[0]?.unit_number || "");
  const [locality, setLocality] = useState(user.addresses?.[0]?.city || "");
  const [state, setState] = useState(user.addresses?.[0]?.state || "");
  const [postcode, setPostcode] = useState(user.addresses?.[0]?.zip || "");
  const [showReferralModal, setShowReferralModal] = useState(false);

  let autocomplete;
  const address2Ref = useRef(null);

  const toInputClass = (readOnly, hasError) => {
    return `h-10 px-2 lg:px-3 py-1 w-full mb-1 rounded-md bg-white placeholder-font-normal placeholder-neutral-600 outline-none ${readOnly ? "opacity-50" : ""} ${hasError ? "border-2 border-red-600" : "border border-neutral-400"}`;
  };

  const autocompleteRef = useCallback((node) => {
    if (node !== null) {
      const fillInAddress = () => {
        // Get the place details from the autocomplete object.
        const place = autocomplete.getPlace();
        let acAddress1 = "";
        let acPostcode = "";
        let acState = "";
        let acLocality = "";

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.
        // place.address_components are google.maps.GeocoderAddressComponent objects
        place.address_components.forEach((component) => {
          const componentType = component.types[0];
          switch (componentType) {
            case GOOGLE_ADDRESS_TYPES.STREET_NUMBER: {
              acAddress1 = `${component.long_name} ${address1}`;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.ROUTE: {
              acAddress1 += component.short_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.POSTAL_CODE: {
              acPostcode = component.long_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.LOCALITY: {
              acLocality = component.long_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.ADMINISTRATIVE_AREA_LEVEL_1: {
              acState = component.short_name;
              break;
            }
            default: break;
          }
        });

        setAddress1(acAddress1 || "");
        setAddress2("");
        setLocality(acLocality || "");
        setState(acState || "");
        setPostcode(acPostcode || "");
        setAddress1Error(acAddress1 ? "" : NO_VALUE_ERROR_MESSAGE);
        setLocalityError(acLocality ? "" : NO_VALUE_ERROR_MESSAGE);
        setStateError(acState ? "" : NO_VALUE_ERROR_MESSAGE);
        setPostcodeError(acPostcode ? "" : NO_VALUE_ERROR_MESSAGE);

        address2Ref.current.focus();
      };
      const initAutocomplete = () => {
        autocomplete = new window.google.maps.places.Autocomplete(node, {
          componentRestrictions: { country: ["us"] },
          fields: ["address_components"],
          types: ["address"],
        });
        // When the user selects an address from the drop-down, populate the
        // address fields in the form.
        autocomplete.addListener("place_changed", fillInAddress);
      };

      initAutocomplete();
    }
  }, []);

  // First Name
  const validateFirstName = (str) => {
    setFirstNameError("");
    if (!str) {
      setFirstNameError(NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    if (!nameRegex.test(str)) {
      setFirstNameError("Please enter a valid first name");
      return false;
    }
    return true;
  };
  const handleFirstNameInput = (e) => {
    const inputString = e.target.value?.trim();
    setFirstName(inputString);
    validateFirstName(inputString);
  };

  // Last Name
  const validateLastName = (str) => {
    setLastNameError("");
    if (!str) {
      setLastNameError(NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    if (!nameRegex.test(str)) {
      setLastNameError("Please enter a valid last name");
      return false;
    }
    return true;
  };
  const handleLastNameInput = (e) => {
    const inputString = e.target.value?.trim();
    setLastName(inputString);
    validateLastName(inputString);
  };

  // DOB
  const validateDob = (date) => {
    setDateOfBirthError("");
    if (!date || !moment(date).isValid()) {
      setDateOfBirthError(NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    if (moment(date) > moment().subtract(18, "years")) {
      setDateOfBirthError(DOB_MIN_AGE_ERROR_MESSAGE);
      return false;
    }
    return true;
  };
  const handleDobInput = (date) => {
    setDob(moment(date).format("YYYY-MM-DD"));
    validateDob(date);
  };

  // Phone Number
  const validatePhoneNumber = (str = "") => {
    setPhoneError("");
    if (!str) {
      setPhoneError(NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    const sanitizedStr = str.startsWith("1") ? str.slice(1) : str;
    if (!isPossiblePhoneNumber(sanitizedStr, "US")){
      setPhoneError(INVALID_PHONE_NUMBER_ERROR_MESSAGE);
      return false;
    }
    return true;
  };
  const handlePhoneNumberInput = (inputString = "") => {
    setPhoneNumber(inputString);
    validatePhoneNumber(inputString);
  };

  // SSN
  const validateSsn = (str) => {
    setSsnError("");
    const sanitizedStr = str.replace(numericalRegex, "");
    if (!sanitizedStr) {
      setSsnError(NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    if (sanitizedStr.length !== 9) {
      setSsnError(INVALID_SSN_ERROR_MESSAGE);
      return false;
    }
    return true;
  };
  const handleSsnInput = (e) => {
    const inputString = e.target.value;
    if (inputString.endsWith("--")) {
      return; // dont set any new value;
    }
    setSSN(inputString);
    validateSsn(inputString);
  };

  const validateConfirmPassword = () => {
    if (password !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return false;
    }
    return true;
  };

  // Password
  const validatePassword = (inputString) => {
    setPasswordError("");
    if (!inputString) {
      setPasswordError(NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    const [passwordValid, passwordErrorMessage] = runPasswordValidator(password);
    if (passwordValid) {
      // eslint-disable-next-line consistent-return
      return validateConfirmPassword();
    }
    setPasswordError(passwordErrorMessage);
    return false;
  };

  const handlePasswordInput = (e) => {
    const inputString = e.target.value;
    setPassword(inputString);
  };

  const handleConfirmPasswordInput = (e) => {
    const inputString = e.target.value;
    setConfirmPassword(inputString);
  };

  const handleBypassAddressChecked = () => {
    setBypassAddress(!bypassAddress);
    setErrorMessage("");
  };

  useEffect(() => {
    if (password) {
      validatePassword(password);
    }
  }, [confirmPassword, password]);

  // Address
  const addressNameValueMap = {
    address1,
    address2,
    locality,
    state,
    postcode,
  };
  const addressSetterMap = {
    address1: setAddress1,
    address2: setAddress2,
    locality: setLocality,
    state: setState,
    postcode: setPostcode,
  };
  const addressErrorSetterMap = {
    address1: setAddress1Error,
    // intentionally omitting address2 to skip validateAddressField
    locality: setLocalityError,
    state: setStateError,
    postcode: setPostcodeError,
  };
  const validateAddressField = (addressFieldName, addressFieldValue) => {
    addressErrorSetterMap[addressFieldName]?.("");
    if ((addressFieldName in addressErrorSetterMap) && !addressFieldValue) {
      addressErrorSetterMap[addressFieldName](NO_VALUE_ERROR_MESSAGE);
      return false;
    }
    return true;
  };
  const validateAllAddressFields = () => {
    const validateAllAddressRes = Object.entries(addressNameValueMap)
      .map(([key, value]) => validateAddressField(key, value));
    return validateAllAddressRes.every((res) => res === true);
  };
  const addressFieldsInputOnChange = (e) => {
    setBypassAddress(false);
    setAddressInvalid(false);
    const { name: addressFieldName, value } = e.target;
    addressSetterMap[addressFieldName](value);
    // validate address field
    validateAddressField(addressFieldName, value);
  };

  const validateAddress = async () => {
    try {
      const { data: { validateAddressV2 } }
      = await client.query({
        fetchPolicy: "network-only",
        query: VALIDATE_ADDRESS,
        variables: {
          address: {
            street1: address1,
            street2: address2,
            city: locality,
            zip: postcode,
            state,
            country: "US",
          },
        },
      });

      if (validateAddressV2.error) {
        setAddressInvalid(true);
        if (!bypassAddress) {
          setErrorMessage(validateAddressV2.error);
          return false;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const submitBasicUser = async () => {
    try {
      await client.mutate({
        mutation: SUBMIT_BASIC_USER_INFO,
        variables: {
          userInfo: {
            firstName,
            lastName,
            phoneNumber: parsePhoneNumberFromString(phoneNumber, "US").number,
            dob: moment(dob).format("YYYY-MM-DD"),
            ...(!user.addresses[0] && {
              address: {
                street1: address1,
                street2: address2,
                city: locality,
                zip: postcode,
                state,
                country: "US",
                bypassAddress,
              },
            }),
            ...(!user.ssnLastFour && { ssn: ssn.replace(numericalRegex, "") }),
            ...(!user.passwordExists && { password: md5(password) }),
          },
        },
      });

      return true;
    } catch (error) {
      if (error.graphQLErrors) {
        setErrorMessage(error.graphQLErrors[0].message);
      } else {
        setErrorMessage("Something went wrong try again.");
      }
      return false;
    }
  };

  const canUserSubmit = () => {
    const presubmissionCheck = [];
    if (!user.firstName) {
      presubmissionCheck.push(validateFirstName(firstName));
    }
    if (!user.lastName) {
      presubmissionCheck.push(validateLastName(lastName));
    }
    if (!user.dob) {
      presubmissionCheck.push(validateDob(dob));
    }
    if (!user.phoneNumber) {
      presubmissionCheck.push(validatePhoneNumber(phoneNumber));
    }
    if (!user.passwordExists) {
      presubmissionCheck.push(validatePassword(password));
    }
    if (!user.ssnLastFour) {
      presubmissionCheck.push(validateSsn(ssn));
    }
    if (!user?.addresses[0]) {
      presubmissionCheck.push(validateAllAddressFields());
    }
    return presubmissionCheck.every((ck) => ck === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    if (!canUserSubmit()) {
      return;
    }
    if (!termsAccepted) {
      setErrorMessage("Please accept terms");
      return;
    }
    try {
      setSubmitting(true);
      if (!user?.addresses[0] && !await validateAddress()) {
        return;
      }
      const success = await submitBasicUser();
      if (success) {
        setCompletedOnboarding(true);
        window.location.reload();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col justify-start items-center bg-neutral-50 text-black">
      <div className="mx-5 my-20 flex flex-col justify-center items-center rounded-xl p-5 md:p-8 max-w-sm lg:max-w-xl">
        <h1 className="text-center font-bold" style={{ fontSize: 28 }}>Complete Enrollment</h1>
        <div id="boost-onboarding-form" className="mt-5 mx-auto w-full">
          <div className="flex flex-col space-y-6 text-black text-sm lg:text-md">
            {/* NAME */}
            <div className="flex justify-between flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-6">
              {buildInputWithLabel({
                component: <input
                  type="text"
                  data-hj-allow
                  name="firstName"
                  placeholder="First name"
                  value={firstName}
                  readOnly={!!user.firstName}
                  maxLength="40"
                  className={toInputClass(!!user.firstName, firstNameError)}
                  onChange={handleFirstNameInput}
                  onBlur={() => validateFirstName(firstName)}
                  disabled={submitting}
                />,
                labelName: "First Name*",
                error: firstNameError,
                className: "grow",
              })}
              {buildInputWithLabel({
                component: <input
                  type="text"
                  data-hj-allow
                  name="lastName"
                  placeholder="Last name"
                  maxLength="40"
                  value={lastName}
                  readOnly={!!user.lastName}
                  className={toInputClass(!!user.lastName, lastNameError)}
                  onChange={handleLastNameInput}
                  onBlur={() => validateLastName(lastName)}
                  disabled={submitting}
                />,
                labelName: "Last Name*",
                error: lastNameError,
                className: "grow",
              })}
            </div>
            {/* DOB + PHONE */}
            <div className="flex items-start justify-between space-x-2 lg:space-x-6">
              {buildInputWithLabel({
                component: <CustomDatePicker
                  selectedDate={dob}
                  handleDateChange={handleDobInput}
                  validateDateChange={() => validateDob(dob)}
                  className={`${toInputClass(!!user.dob, dateOfBirthError)} mt-0 w-full`}
                  disabled={!!user.dob || submitting}
                />,
                labelName: "Date of birth*",
                error: dateOfBirthError,
                className: "grow",
              })}
              {buildInputWithLabel({
                component: <CustomPhoneNumberInput
                  readOnly={!!user.phoneNumber}
                  className={toInputClass(!!user.phoneNumber, phoneError)}
                  phoneNumber={phoneNumber}
                  setPhoneNumber={handlePhoneNumberInput}
                  validatePhoneNumber={() => validatePhoneNumber(phoneNumber)}
                  disabled={submitting}
                />,
                labelName: "Phone number*",
                error: phoneError,
                className: "grow",
              })}
            </div>
            {/* ADDRESS - street + unit */}
            <div className="flex justify-between items-start space-x-2 lg:space-x-6">
              {buildInputWithLabel({
                component: <input
                  ref={user.addresses[0] ? null : autocompleteRef}
                  type="text"
                  data-hj-allow
                  name="address1"
                  placeholder="Street address"
                  value={address1}
                  readOnly={!!user.addresses[0]}
                  className={toInputClass(!!user.addresses[0], address1Error)}
                  onChange={addressFieldsInputOnChange}
                  onBlur={() => validateAddressField("address1", address1)}
                  disabled={submitting}
                />,
                labelName: "Street address*",
                error: address1Error,
                className: "flex-2",
              })}
              {buildInputWithLabel({
                component: <input
                  type="text"
                  data-hj-allow
                  name="address2"
                  maxLength="14"
                  ref={address2Ref}
                  value={address2}
                  placeholder="Apt"
                  onChange={addressFieldsInputOnChange}
                  readOnly={!!user.addresses[0]}
                  className={toInputClass(!!user.addresses[0])}
                  style={{ minWidth: 60 }}
                  disabled={submitting}
                />,
                labelName: "Apt",
                className: "flex-1",
              })}
            </div>
            {/* ADDRESS - city, state, zip */}
            <div className="flex flex-col space-y-4 lg:flex-row lg:space-x-6 lg:space-y-0 lg:items-start">
              {buildInputWithLabel({
                component: <input
                  type="text"
                  data-hj-allow
                  name="locality"
                  placeholder="City"
                  value={locality}
                  onChange={addressFieldsInputOnChange}
                  onBlur={() => validateAddressField("locality", locality)}
                  readOnly={!!user.addresses[0]}
                  className={toInputClass(!!user.addresses[0], localityError)}
                  disabled={submitting}
                />,
                labelName: "City*",
                error: localityError,
                className: "w-full",
              })}
              <div className="flex justify-between space-x-6">{buildInputWithLabel({
                component: <select
                  name="state"
                  id="state"
                  className={`${toInputClass(!!user.addresses[0], stateError)} ${state ? "" : "text-gray-600"}`}
                  onChange={addressFieldsInputOnChange}
                  onBlur={() => validateAddressField("state", state)}
                  style={{ minWidth: 100 }}
                  disabled={submitting}
                  defaultValue=""
                  value={state}
                >
                  <option disabled value="" key="state-initial">State</option>
                  {
                    STATES.map((s) => <option value={s.value} key={`state-${s.value}`}>{s.value}</option>)
                  }
                </select>,
                labelName: "State*",
                className: "grow",
                error: stateError,
              })}
              {buildInputWithLabel({
                component: <input
                  type="text"
                  data-hj-allow
                  name="postcode"
                  value={postcode.replace(numericalRegex, "").slice(0,5)}
                  placeholder="Zip"
                  onChange={addressFieldsInputOnChange}
                  onBlur={() => validateAddressField("postcode", postcode)}
                  readOnly={!!user.addresses[0]}
                  className={toInputClass(!!user.addresses[0], postcodeError)}
                  style={{ minWidth: 85 }}
                  disabled={submitting}
                />,
                labelName: "Zip*",
                error: postcodeError,
              })}
              </div>
            </div>
            {/* SSN */}
            {!user.ssnLastFour && (
              <div className="mt-2">
                <div className="flex relative items-center">
                  {buildInputWithLabel({
                    component: (
                      <div className="relative">
                        <input
                          type={ssnInputType}
                          data-hj-allow
                          name="ssn"
                          placeholder="___-__-____"
                          maxLength="11"
                          value={formatSSN(ssn)}
                          className={toInputClass(!!user.ssnLastFour, ssnError)}
                          onChange={handleSsnInput}
                          onBlur={() => validateSsn(ssn)}
                          disabled={submitting}
                        />
                        <div className="absolute top-0 right-0 z-10 h-full flex items-center mr-2 pb-1">
                          <ToggleVisibility
                            iconColorClassNameShowing="text-black hover:text-neutral-600"
                            iconColorClassNameHidden="text-black hover:text-neutral-600"
                            visible={ssnInputType !== "password"}
                            onClick={() => {
                              const type =
                                ssnInputType === "input" ? "password" : "input";
                              setSsnInputType(type);
                            }}
                          />
                        </div>
                      </div>
                    ),
                    labelName: "Social security number (SSN)*",
                    error: ssnError,
                    className: "grow",
                  })}
                </div>
              </div>
            )}
            {/* PASSWORD */}
            {!user.passwordExists && (
              <div className="mt-2">
                {buildInputWithLabel({
                  component:
                  <div className="flex flex-col space-y-2">
                    <div className="flex relative items-center">
                      <input
                        type={createPasswordType}
                        data-hj-allow
                        name="password"
                        placeholder="Create password"
                        value={password}
                        className={`${toInputClass(null, passwordError)} data-hj-allow`}
                        onChange={handlePasswordInput}
                        onBlur={() => validatePassword(password)}
                        disabled={submitting}
                      />
                      <div className="absolute top-0 right-0 z-10 h-full flex items-center mr-2 pb-1">
                        <ToggleVisibility
                          visible={createPasswordType !== "password"}
                          iconColorClassNameShowing="text-black hover:text-neutral-600"
                          iconColorClassNameHidden="text-black hover:text-neutral-600"
                          onClick={() => {
                            const type = createPasswordType === "input" ? "password" : "input";
                            setCreatePasswordType(type);
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex relative items-center">
                      <input
                        type={createPasswordType}
                        data-hj-allow
                        name="confirm"
                        placeholder="Confirm password"
                        value={confirmPassword}
                        className={`${toInputClass(null, passwordError)} data-hj-allow`}
                        onChange={handleConfirmPasswordInput}
                        onBlur={handleConfirmPasswordInput}
                        disabled={submitting}
                      />
                    </div>
                  </div>,
                  labelName: "Password*",
                  error: passwordError,
                })}
                <p className="mt-1 w-full text-sm data-hj-allow">
                  Must be at least 8 characters. Use at least one number, one special character.
                </p>
              </div>
            )}
            {
              errorMessage && (
                <div className="mt-4 flex justify-center">
                  <p className="text-red-600">{errorMessage}</p>
                </div>
              )
            }
            {
              addressInvalid && (
                <Checkbox
                  checked={bypassAddress}
                  onChange={handleBypassAddressChecked}
                  className="mt4 mx-4 w-8 h-8 rounded-lg"
                  labelComponent={
                    <p className="text-sm">
                      I understand that if this address is incorrect, it may impact TomoBoost&apos;s ability to report.
                    </p>
                  }
                />
              )
            }
            {/* Continue button */}
            <LegalAgreement
              containerClassName="mt-4 text-sm my-4 flex items-start"
              checkboxClassName="custom-checkbox"
              linkClassName="text-boost-lighter-purple hover:text-boost-light-purple"
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
            />
            <div className="text-center flex justify-center">
              <button
                className="bg-black hover:bg-neutral-600 focus:bg-neutral-700 focus:outline-none px-16 py-2 text-white"
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ?
                  (<div className="loader" style={{ borderLeftColor: "#4B2BA3", width: 20, height: 20 }} />) :
                  (<span>Submit</span>)
                }
              </button>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <InviteFriends
            referralCode={referralCode}
            currentTerm={currentProductTerm}
            currentTier={currentProductTier}
          />
        </div>
      </div>
    </div>
  );
};

export default BoostOnboarding;
